/**
 * Full registry brand card (GET /brands/:idOrSlug) — program, platform, every external
 * identifier with its kind/scope, expected events, and event aggregates. Open to any
 * authenticated role; the backend access-filters the same way GET /brands does (403 if a
 * viewer has no subscription to this brand).
 *
 * docs/solutions/brand-registry.md is explicit that slug (the historical ?brand= value) and
 * display_name are allowed to diverge (bitzbet/BlitzBet, spinational/Spinnational) — this is
 * domain data, not an error, so both render with the same neutral styling and a plain note
 * explains the difference instead of flagging it.
 */
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Ban, Tag } from 'lucide-react';
import { haptic } from '@/shared/utils/telegram';
import { formatRelativeTime, formatInteger } from '@/shared/utils/formatting';
import { useBrandCard } from '@/features/registry/hooks/useRegistry';
import { ApiError } from '@/shared/api/client';

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-600',
  paused: 'bg-amber-500/10 text-amber-600',
  archived: 'bg-black/5 dark:bg-white/10 text-[var(--tg-theme-hint-color)]',
};

const CONFIDENCE_STYLES: Record<string, string> = {
  confirmed: 'bg-emerald-500/10 text-emerald-600',
  operational: 'bg-blue-500/10 text-blue-600',
  assumed: 'bg-amber-500/10 text-amber-600',
};

export function BrandCardPage() {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const navigate = useNavigate();
  const { data: brand, isLoading, error } = useBrandCard(idOrSlug);

  const handleBack = () => {
    haptic.impact('light');
    navigate(-1);
  };

  const status = brand?.status ? (
    <span
      className={`text-[10px] font-black uppercase px-2 py-1 rounded-full ${STATUS_STYLES[brand.status] ?? 'bg-black/5 dark:bg-white/10 text-[var(--tg-theme-hint-color)]'}`}
    >
      {brand.status}
    </span>
  ) : null;

  return (
    <div className="flex flex-col gap-5 pb-24 animate-in fade-in slide-in-from-right duration-300">
      <div className="sticky top-0 z-10 bg-[var(--tg-theme-bg-color)] pt-2 pb-2 flex items-center gap-3">
        <button
          onClick={handleBack}
          className="p-2 rounded-full hover:bg-[var(--tg-theme-secondary-bg-color)] transition-colors"
          aria-label="Back"
        >
          <ArrowLeft size={20} className="text-[var(--tg-theme-hint-color)]" />
        </button>
        <h1 className="text-2xl font-black uppercase tracking-tighter flex-1 truncate">
          {brand?.displayName ?? 'Brand'}
        </h1>
        {status}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--tg-theme-link-color)] border-t-transparent" />
        </div>
      )}

      {error && <ErrorState error={error} />}

      {brand && (
        <>
          {/* Identity: slug vs display name, treated as ordinary domain data */}
          <Section title="Identity">
            <Field label="Display name" value={brand.displayName} />
            <Field label="Slug (sent as ?brand=)" value={brand.slug} mono />
            {brand.slug.toLowerCase() !== brand.displayName.toLowerCase() && (
              <p className="text-[11px] text-[var(--tg-theme-hint-color)] font-medium">
                The slug and display name differ — that is expected, the slug is the historical link
                value and stays fixed even when the brand is renamed.
              </p>
            )}
          </Section>

          {/* Program & platform */}
          <Section title="Program">
            {brand.program ? (
              <>
                <Field label="Name" value={brand.program.name} />
                <Field label="Code" value={brand.program.code} mono />
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full bg-[var(--tg-theme-link-color)]/10 text-[var(--tg-theme-link-color)]">
                    {brand.program.platform}
                  </span>
                  <span
                    className={`text-[10px] font-black uppercase px-2 py-1 rounded-full ${STATUS_STYLES[brand.program.status] ?? 'bg-black/5 dark:bg-white/10 text-[var(--tg-theme-hint-color)]'}`}
                  >
                    {brand.program.status.replace(/_/g, ' ')}
                  </span>
                </div>
              </>
            ) : (
              <p className="text-xs text-[var(--tg-theme-hint-color)]">No program assigned yet</p>
            )}
          </Section>

          {/* Identifiers */}
          <Section title={`Identifiers (${brand.identifiers.length})`}>
            {brand.identifiers.length === 0 ? (
              <p className="text-xs text-[var(--tg-theme-hint-color)]">
                No external identifiers recorded
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {brand.identifiers.map((id, i) => (
                  <div
                    key={`${id.kind}-${id.value}-${i}`}
                    className="flex items-center justify-between gap-3 p-3 rounded-xl bg-black/[0.03] dark:bg-white/[0.04]"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <Tag
                          size={11}
                          className="text-[var(--tg-theme-hint-color)] flex-shrink-0"
                        />
                        <span className="text-[10px] font-black uppercase text-[var(--tg-theme-hint-color)] tracking-wide">
                          {id.kind.replace(/_/g, ' ')}
                        </span>
                        {id.isPrimary && (
                          <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-[var(--tg-theme-link-color)]/15 text-[var(--tg-theme-link-color)]">
                            primary
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-bold font-mono text-[var(--tg-theme-text-color)] truncate mt-0.5">
                        {id.value}
                      </p>
                      {id.sourcePlatform && (
                        <p className="text-[10px] text-[var(--tg-theme-hint-color)] font-medium mt-0.5">
                          scoped to {id.sourcePlatform}
                        </p>
                      )}
                    </div>
                    {id.confidence && (
                      <span
                        className={`flex-shrink-0 text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${CONFIDENCE_STYLES[id.confidence] ?? 'bg-black/5 dark:bg-white/10 text-[var(--tg-theme-hint-color)]'}`}
                      >
                        {id.confidence}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Expected events */}
          {brand.expectedEvents && brand.expectedEvents.length > 0 && (
            <Section title="Expected events">
              <div className="flex flex-wrap gap-1.5">
                {brand.expectedEvents.map((event) => (
                  <span
                    key={event}
                    className="text-[10px] font-black uppercase px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600"
                  >
                    {event}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {/* Event aggregates */}
          <Section title="Events">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-xl bg-black/[0.03] dark:bg-white/[0.04]">
                <p className="text-[10px] font-black text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
                  Total
                </p>
                <p className="text-xl font-black tabular-nums">
                  {formatInteger(brand.events.totalEvents)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-black/[0.03] dark:bg-white/[0.04]">
                <p className="text-[10px] font-black text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
                  Last seen
                </p>
                <p className="text-sm font-bold">{formatRelativeTime(brand.events.lastEventAt)}</p>
              </div>
            </div>

            {brand.events.byEvent.length > 0 && (
              <div className="flex flex-col gap-1.5 mt-3">
                {brand.events.byEvent.map((row) => (
                  <div key={row.event} className="flex items-center justify-between text-xs">
                    <span className="font-bold text-[var(--tg-theme-hint-color)] uppercase tracking-wide">
                      {row.event}
                    </span>
                    <span className="font-black tabular-nums">{formatInteger(row.count)}</span>
                  </div>
                ))}
              </div>
            )}

            {brand.events.sources.length > 0 && (
              <p className="text-[11px] text-[var(--tg-theme-hint-color)] font-medium mt-2">
                Sources: {brand.events.sources.join(', ')}
              </p>
            )}
          </Section>

          {brand.notes && (
            <Section title="Notes">
              <p className="text-xs text-[var(--tg-theme-text-color)]">{brand.notes}</p>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-2xl p-4 space-y-2 border border-black/5 dark:border-white/5">
      <h2 className="text-[10px] font-black text-[var(--tg-theme-hint-color)] uppercase tracking-widest">
        {title}
      </h2>
      {children}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-[var(--tg-theme-hint-color)] font-medium">{label}</span>
      <span
        className={`text-sm font-bold text-[var(--tg-theme-text-color)] truncate ${mono ? 'font-mono' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

function ErrorState({ error }: { error: unknown }) {
  const status = error instanceof ApiError ? error.status : undefined;

  if (status === 404) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Tag size={40} className="text-[var(--tg-theme-hint-color)]" />
        <p className="text-sm font-bold text-[var(--tg-theme-hint-color)] text-center px-6">
          This brand isn't in the registry yet
        </p>
      </div>
    );
  }

  if (status === 403) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Ban size={40} className="text-[var(--tg-theme-hint-color)]" />
        <p className="text-sm font-bold text-[var(--tg-theme-hint-color)] text-center px-6">
          You don't have access to this brand
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 mx-1 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-center">
      <AlertCircle size={28} className="text-rose-600 mx-auto mb-2" />
      <p className="text-sm font-bold text-rose-600">Failed to load brand</p>
    </div>
  );
}
